import { NodeSDK } from '@opentelemetry/sdk-node';
import { BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-node';
import { detectResources, MaybePromise } from '@opentelemetry/resources';
import {
  trace,
  context,
  propagation,
  SpanStatusCode,
  Span,
  INVALID_SPAN_CONTEXT,
  TraceFlags,
} from '@opentelemetry/api';
import {
  logs
} from '@opentelemetry/api-logs';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PlatformDetector } from './resource-detector';
import { CustomExporter } from './log-exporter';

import type { LogAttributes } from '../type';
import { mapSeverity, mapSeverityText } from '../utils/severityMap';
import { CustomTraceExporter } from './trace-exporter';
import { isObservable } from '../utils/isObservable';
import { TraceDropToken } from '../const';

export class AppOTelSDK {
  private sdk: NodeSDK | null = null;
  private logProcessor: BatchLogRecordProcessor | null = null;
  private spanProcessor: BatchSpanProcessor | null = null;
  private logger: ReturnType<typeof logs.getLogger> | null = null;
  private isStarted = false;
  private static CUSTOM_FORMAT_REGEX = /^[0-9a-fA-F]{32}-[0-9a-fA-F]{16}$/;

  public static emptySpan: Span = trace.wrapSpanContext(INVALID_SPAN_CONTEXT);

  start() {
    if (this.isStarted) return;
    this.isStarted = true;
    const resource = detectResources({
      detectors: [new PlatformDetector()],
    });

    // 将获取上下文的函数传入 Exporter，保持 SDK 对框架无感
    const logExporter = new CustomExporter();

    this.logProcessor = new BatchLogRecordProcessor(logExporter, {
      scheduledDelayMillis: 2000,
      // 防止日志聚合过长被截断导致丢失
      maxExportBatchSize: 1,
    });

    // 2. Trace 配置
    const traceExporter = new CustomTraceExporter();
    this.spanProcessor = new BatchSpanProcessor(traceExporter, {
      scheduledDelayMillis: 2000,
      // 防止 Trace 聚合过长被截断导致丢失
      maxExportBatchSize: 1,
    });

    this.sdk = new NodeSDK({
      resource,
      logRecordProcessor: this.logProcessor,
      spanProcessor: this.spanProcessor,
    });

    this.sdk.start();
    this.logger = logs.getLogger('app-logger');
  }

  /**
   * [框架专用 API] 开启一个根 Span (Root Span)
   * 该方法专供 Middleware/Interceptor 使用
   * wrapper 形式
   */
  public startContext(
    headers: Record<string, string>,
    name: string,
    fn: (span: Span) => void
  ) {
    const carrier: Record<string, string> = {};
    // 1. 提取 Header 中与服务端约定好的 header 字段
    const customTraceId = headers['rpc-persist-apaas-observability-trace'];
    if (customTraceId && AppOTelSDK.CUSTOM_FORMAT_REGEX.test(customTraceId)) {
      // 转换为官方标准的 traceparent
      carrier["traceparent"] = `00-${customTraceId}-01`;
    }
    // 使用官方 SDK 进行上下文提取
    const activeContext = propagation.extract(context.active(), carrier);
    const tracer = trace.getTracer('app-core');
    // 2. 开启 Active Span，并执行回调
    // 这里不返回 Promise，因为 Middleware 的 next() 是同步调用的风格
    tracer.startActiveSpan(name, {}, activeContext, (span) => {
      fn(span);
    });
  }

  /**
   * [框架专用 API] 开启一个根 Span (Root Span)
   * 该方法专供 Middleware/Interceptor 使用
   * wrapper 形式
   */
  public startRootTrace(
    headers: Record<string, string>,
    name: string,
  ) {
    const carrier: Record<string, string> = {};
    // 1. 提取 Header 中与服务端约定好的 header 字段
    const customTraceId = headers['rpc-persist-apaas-observability-trace'];
    if (customTraceId && AppOTelSDK.CUSTOM_FORMAT_REGEX.test(customTraceId)) {
      // 转换为官方标准的 traceparent
      carrier["traceparent"] = `00-${customTraceId}-01`;
    }
    // 使用官方 SDK 进行上下文提取
    const activeContext = propagation.extract(context.active(), carrier);
    const tracer = trace.getTracer('app-core');
    // 2. 开启 Active Span，并执行回调
    // 这里不返回 Promise，因为 Middleware 的 next() 是同步调用的风格
    const rootSpan = tracer.startSpan(name, {}, activeContext);
    return rootSpan;
  }

  /**
   * [用户专用 API] 手动打点 (Child Span)
   * 用户在 Service 中调用此方法，会自动挂载到 Middleware 创建的 Root Span 下
   */
  trace<T>(name: string, fn: (span: Span) => MaybePromise<T> | Observable<T>): MaybePromise<T> | Observable<T> {
    const tracer = trace.getTracer('app-core');
    // 1. 启动 Span
    const span = tracer.startSpan(name);
    // 2. 进入 Context 上下文
    return context.with(trace.setSpan(context.active(), span), () => {
      try {
        // 3. 执行业务逻辑
        const result = fn(span);
        // Observable (流式)
        if (isObservable(result)) {
          return result.pipe(
            finalize(() => span.end())
          );
        }
        //  Promise (异步)
        if (result instanceof Promise) {
          return result
            .then((res) => {
              span.setStatus({ code: SpanStatusCode.OK });
              return res;
            })
            .finally(() => span.end());
        }
        // 同步
        span.setStatus({ code: SpanStatusCode.OK });
        span.end();
        return result;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        // 捕获同步错误
        span.end();
        throw err;
      }
    });
  }

  /**
   * [Log API] 自动关联 TraceContext
   */
  public log(level: string, message: string, extra: Record<string, unknown> = {}, spanAttr?: {
    traceId: string;
    spanId: string;
  }) {
    try {
      if (!this.logger) return;
      const severityNumber = mapSeverity(level);
      const severityText = mapSeverityText(level);

      const attributes: LogAttributes = {
        ...(extra || {}),
        pid: process.pid,
      };

      let logContext = context.active();
      try {
        if (spanAttr) {
          const spanContext = {
            ...spanAttr,
            // 通常设为 SAMPLED (1)，表示这是一个被采样的链路，否则日志可能会被后端丢弃
            traceFlags: TraceFlags.SAMPLED,
            isRemote: false,
          };
          // Logger.emit 需要一个 Context
          logContext = trace.setSpan(context.active(), trace.wrapSpanContext(spanContext));
        }
      } catch {
        logContext = context.active();
      }

      this.logger.emit({
        body: message,
        severityNumber,
        severityText,
        attributes,
        timestamp: new Date(),
        // 自动关联当前上下文
        context: logContext,
      });
    } catch {
      // 静默处理，不影响用户逻辑
    }
  }

  /**
 * 启动 Span 的工厂函数
 * @param {string} name - Span 名称
 * @param {Object} [parentSpan] - (可选) 父 Span 对象
 */
  public startTrace(name: string, parentSpan?: Span): Span {
    try {
      const tracer = trace.getTracer('app-core');
      let ctx = context.active();
      // 如果用户传了父 Span，将其设为当前 Context
      if (parentSpan) {
        ctx = trace.setSpan(ctx, parentSpan);
      }
      const span = tracer.startSpan(name, undefined, ctx);
      return span;
    } catch {
      // 错误兜底，防止报错影响用户原有逻辑执行
      return AppOTelSDK.emptySpan
    }

  }

  /**
  * 丢弃 Span 的工厂函数
  * @param {Span} span - 要被丢弃的 Span 对象
  */
  public static dropTraceSpan(span: Span): void {
    span.setAttribute(TraceDropToken, true)
  }

  /**
   * [框架专用] 强制刷新
   */
  async flush() {
    await Promise.all([
      this.logProcessor?.forceFlush(),
      this.spanProcessor?.forceFlush()
    ]);
  }
}

export const appSdk = new AppOTelSDK();
